// Quote detail page — M2 Phase 5.4 quote builder. Route: /quotes/:id
//
// Three tabs:
//   - Builder (default) — the actual quote-assembly surface. Quote-level
//     qty tier editor + part list with inline price-per-tier editing,
//     embedded PartWizard for spec edits, and a totals card.
//   - Pricing — bigger totals card + per-part price/margin breakdown table.
//   - Activity — _audit[] timeline (mirrors PartDetailPage's History tab).
//
// State model:
//   - The loaded Quote is the source of truth for what's persisted. Edits
//     write straight back via updateQuote(); we re-load() after to keep
//     the on-screen number in sync with the data layer's updatedAt stamp.
//   - The loaded Parts map is keyed by partId — used for descriptions,
//     PartTypeBadge, and the *live* materialCost cost basis for margin
//     warnings. Adding a new part to the quote re-fetches the part record
//     so the parts map covers it before the price snapshot is seeded.
//
// Pricing math lives in @/shared/quotePricing — this file just glues the
// inputs together and renders.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getCustomer,
  getPart,
  getQuote,
  listParts,
  updatePart,
  updateQuote,
} from '@/shared/crm';
import type {
  AuditEntry,
  Customer,
  JobType,
  Machine,
  Part,
  PartQuotedSpec,
  PartType,
  Quote,
} from '@/shared/crmTypes';
import {
  computeFullCostByTier,
  computeMarginsByTier,
  computeTotalsByTier,
  computePartCost,
  DEFAULT_MARKUP_PCT,
  MARGIN_WARNING_THRESHOLD,
  suggestPricePerPiece,
} from '@/shared/quotePricing';
import { listMachines } from '@/shared/machines';
import { PartTypeBadge, PART_TYPE_META } from '@/components/PartTypeBadge';
import { QuoteStatusBadge } from '@/components/QuoteStatusBadge';
import { TierBadge } from '@/components/TierBadge';
import { NewPartModal } from '@/components/NewPartModal';
import { PartWizard } from '@/components/PartWizard/PartWizard';
import { QuotePdfActions } from '@/components/QuotePdfActions';
import { PdfViewerModal } from '@/components/PdfViewerModal';
import { PartCardImage } from '@/components/PartCardImage';
import { QuoteAttachmentsPanel } from '@/components/QuoteAttachmentsPanel';
import { PartAttachmentPickerModal } from '@/components/PartAttachmentPickerModal';
import { uploadQuoteAttachment } from '@/shared/drawingStorage';

type Tab = 'builder' | 'pricing' | 'activity';

const TABS: Array<{ key: Tab; label: string }> = [
  { key: 'builder', label: 'Builder' },
  { key: 'pricing', label: 'Pricing' },
  { key: 'activity', label: 'Activity' },
];

const JOB_TYPE_LABELS: Record<JobType, { label: string; icon: string }> = {
  multi_part_production: { label: 'Multi-part production', icon: '⚙' },
  prototype: { label: 'Prototype', icon: '💡' },
  one_off: { label: 'One-off', icon: '🔩' },
  quick_quote: { label: 'Quick quote', icon: '⚡' },
  spot_quote: { label: 'Spot quote', icon: '🔍' },
  welding_job: { label: 'Welding job', icon: '🧯' },
  machine_build: { label: 'Machine build', icon: '🏭' },
};

function fmtCurrency(n: number | undefined | null, opts?: { fractionDigits?: number }): string {
  if (n === undefined || n === null || !Number.isFinite(n)) return '—';
  const f = opts?.fractionDigits ?? 0;
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: f,
    maximumFractionDigits: f,
  });
}

function fmtRelativeAgo(iso: string): string {
  const diffSec = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diffSec < 60) return `${Math.round(diffSec)}s ago`;
  if (diffSec < 3600) return `${Math.round(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.round(diffSec / 3600)}h ago`;
  return `${Math.round(diffSec / 86400)}d ago`;
}

function parseQtyTiers(raw: string): number[] | null {
  const nums = raw
    .split(/[^\d]+/)
    .map((s) => s.trim())
    .filter(Boolean)
    .map((s) => Number.parseInt(s, 10));
  if (nums.length === 0 || nums.length > 5) return null;
  if (nums.some((n) => !Number.isFinite(n) || n <= 0)) return null;
  return nums;
}

/**
 * Resolve the markup multiplier for a part. Today the only part-level
 * markup we have on the typed model is `purchasedSource.markupPct` (set
 * by the MarkupStep in the wizard for type==='purchased' parts). For
 * everything else, fall back to DEFAULT_MARKUP_PCT.
 *
 * Quote-level markup override is reserved for future use — when that
 * field lands on Quote, plumb it through here.
 *
 * `purchasedSource.markupPct` is stored as a PERCENT (e.g. 65 for 65%),
 * not a multiplier. Convert.
 */
function resolveMarkupPct(part: Part): number {
  const purchasedPct = part.purchasedSource?.markupPct;
  if (typeof purchasedPct === 'number' && purchasedPct > 0) {
    return 1 + purchasedPct / 100;
  }
  return DEFAULT_MARKUP_PCT;
}

/**
 * Seed a fresh PartQuotedSpec from a Part using the live cost engine.
 * Walks every tier on the quote, computes material + amortized setup +
 * machine run-time + outside services, applies markup, and freezes the
 * result into pricePerTier. Estimated setup / run hours and the material
 * snapshot get filled in so the spec carries an honest "what we promised"
 * record.
 *
 * Two notes on the snapshot fields:
 *   - `estimatedRunHrs` is the sum across all machines at tier 0 (the
 *     lowest-qty tier — usually the prototype run). Folding the tier-
 *     variant runtime into a single field needs a real schema change;
 *     tier-0 is what the v1 PDF used.
 *   - `materialCostSnapshot` is the per-piece material cost at tier 0,
 *     normalized through the same fall-through the snapshot helpers use.
 *     CSM parts contribute the handling fee there.
 */
function seedSnapshotFromPart(
  part: Part,
  quoteTiers: number[],
  machinesByName: Record<string, Machine>,
): PartQuotedSpec {
  const markup = resolveMarkupPct(part);

  const pricePerTier = quoteTiers.map((qty, t) => {
    const cost = computePartCost(part, qty, t, machinesByName);
    const price = suggestPricePerPiece(cost, markup);
    // Round to cents — same scale the inline editor stores at.
    return Math.round(price * 100) / 100;
  });

  // Aggregate machine hrs for the snapshot's "estimated" fields. Runtime
  // is tier-0 only (see fn header).
  const setupTotalHrs =
    (part.machines ?? []).reduce((s, m) => s + (m.setupHrs ?? 0), 0)
    + (part.setupHrs ?? 0);
  const runHrsAtTier0 =
    (part.machines ?? []).reduce((s, m) => s + (m.hrsPerTier?.[0] ?? 0), 0);

  // Material snapshot at tier 0 — single dollar value. Run through the
  // same engine so CSM / per-bar / per-sheet all collapse correctly.
  const tier0Cost = computePartCost(
    part,
    quoteTiers[0] ?? 0,
    0,
    machinesByName,
  );

  return {
    qtyTiers: part.qtyTiers ?? quoteTiers,
    estimatedSetupHrs: setupTotalHrs,
    estimatedRunHrs: runHrsAtTier0,
    materialCostSnapshot: tier0Cost.materialPerPiece,
    pricePerTier,
  };
}

export function QuoteDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [quote, setQuote] = useState<Quote | null>(null);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [partsById, setPartsById] = useState<Record<string, Part>>({});
  // Machines keyed by NAME (not id) — Part.machines[].machine carries the
  // display name, not a typed FK, so the cost engine joins on name. Loaded
  // once on mount; we don't reload when a single quote saves because the
  // machine matrix changes rarely and a Settings save will trigger its own
  // reload path. activeOnly:true to keep retired machines out of new prices
  // (existing snapshots that reference an inactive machine still compute —
  // they just look up against the loaded map; inactive ones drop to $0 with
  // the silent-skip behavior in computePartCost).
  const [machinesByName, setMachinesByName] = useState<Record<string, Machine>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('builder');

  // Track which part is in inline-edit mode, plus its working PartWizard
  // draft. Only one part is editable at a time — keeps the UI focused.
  const [editingPartId, setEditingPartId] = useState<string | null>(null);
  const [partDraft, setPartDraft] = useState<(Partial<Part> & { type: PartType }) | null>(null);

  const [addPartModalOpen, setAddPartModalOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  // Per-part attachment-picker modal — opens when the user clicks the 📎 N
  // button on a part row. Only one part is being picked-for at a time.
  const [attachmentPickerPartId, setAttachmentPickerPartId] = useState<string | null>(null);

  // PDF preview state — generated blob lives here until the user closes the
  // viewer or generates a new one. Cleared on quote unload.
  const [pdfPreviewBlob, setPdfPreviewBlob] = useState<Blob | null>(null);
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const q = await getQuote(id);
      setQuote(q);
      if (q) {
        // Load customer + every referenced part in parallel. Failures on
        // individual parts are swallowed so a single soft-deleted part
        // doesn't blank the whole page.
        const [cust, parts] = await Promise.all([
          q.customerId ? getCustomer(q.customerId) : Promise.resolve(null),
          Promise.all((q.partIds ?? []).map((pid) => getPart(pid).catch(() => null))),
        ]);
        setCustomer(cust);
        const map: Record<string, Part> = {};
        parts.forEach((p) => {
          if (p) map[p.id] = p;
        });
        setPartsById(map);
      }
    } catch (e) {
      setError(`Failed to load quote: ${(e as Error).message}`);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  // Load the machine matrix once on mount. Separate from the quote load so
  // we don't re-fetch the dozen-ish rows on every quote-save reload. If the
  // user opens Settings and edits a machine in another tab while this view
  // is open, the new rates won't show until the page refreshes — that's an
  // acceptable trade since the rates change rarely. If it bites, swap to
  // observeMachines (not built yet) or trigger a re-fetch on focus.
  useEffect(() => {
    let cancelled = false;
    listMachines({ activeOnly: true })
      .then((list) => {
        if (cancelled) return;
        const map: Record<string, Machine> = {};
        for (const m of list) map[m.name] = m;
        setMachinesByName(map);
      })
      .catch(() => {
        // Silent-fail — empty machine map means the cost engine returns $0
        // for setup/run lines, which is the same fallback computePartCost
        // already uses for unmapped machines. The seed will still produce
        // a price (material + outside services), just not a complete one.
        if (!cancelled) setMachinesByName({});
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="bg-paper border border-line rounded p-8 text-center text-text-mute animate-pulse">
          Loading quote…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="bg-orange/10 border-l-4 border-orange text-text px-3 py-2 text-sm mb-4 flex items-center justify-between gap-3">
          <span>⚠ {error}</span>
          <button
            type="button"
            onClick={load}
            className="text-xs text-orange font-semibold hover:underline shrink-0"
          >
            Retry
          </button>
        </div>
        <button
          type="button"
          onClick={() => navigate('/quotes')}
          className="text-sm text-orange hover:underline"
        >
          ← Back to quotes
        </button>
      </div>
    );
  }

  if (!quote) {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="bg-paper border border-line rounded p-8 text-center">
          <div className="text-4xl mb-2">🔍</div>
          <div className="font-semibold text-navy mb-1">Quote not found</div>
          <p className="text-sm text-text-mute mb-4">
            ID <span className="font-mono">{id}</span> doesn't exist or was soft-deleted.
          </p>
          <button
            type="button"
            onClick={() => navigate('/quotes')}
            className="px-3 py-1.5 bg-orange text-white text-sm rounded font-semibold hover:opacity-90"
          >
            ← Back to quotes
          </button>
        </div>
      </div>
    );
  }

  // ───── handlers wired against the loaded quote ─────

  async function handleQtyTiersChange(raw: string) {
    if (!quote) return;
    const parsed = parseQtyTiers(raw);
    if (!parsed) return;
    // Don't touch partQuotedSpecs — surfaces as a warning so Justin
    // explicitly re-prices each part when the tier shape changes.
    await updateQuote(quote.id, { qtyTiers: parsed });
    await load();
  }

  async function handleAddExistingPart(part: Part) {
    if (!quote) return;
    if (quote.partIds?.includes(part.id)) {
      setPickerOpen(false);
      return; // already on the quote — no-op
    }
    const nextSpecs = { ...(quote.partQuotedSpecs ?? {}) };
    nextSpecs[part.id] = seedSnapshotFromPart(part, quote.qtyTiers ?? [], machinesByName);
    await updateQuote(quote.id, {
      partIds: [...(quote.partIds ?? []), part.id],
      partQuotedSpecs: nextSpecs,
    });
    setPickerOpen(false);
    await load();
  }

  async function handlePartCreated(partId: string) {
    if (!quote) return;
    // Re-fetch the just-created part so we can seed a snapshot from its
    // (likely empty) materialCost. NewPartModal closes itself; we just
    // need to wire it onto the quote.
    const part = await getPart(partId);
    if (!part) {
      setAddPartModalOpen(false);
      return;
    }
    const nextSpecs = { ...(quote.partQuotedSpecs ?? {}) };
    nextSpecs[part.id] = seedSnapshotFromPart(part, quote.qtyTiers ?? [], machinesByName);
    await updateQuote(quote.id, {
      partIds: [...(quote.partIds ?? []), part.id],
      partQuotedSpecs: nextSpecs,
    });
    setAddPartModalOpen(false);
    await load();
  }

  async function handleRemovePart(partId: string) {
    if (!quote) return;
    if (!window.confirm('Remove this part from the quote? Its frozen pricing will be lost.')) {
      return;
    }
    const nextSpecs = { ...(quote.partQuotedSpecs ?? {}) };
    delete nextSpecs[partId];
    await updateQuote(quote.id, {
      partIds: (quote.partIds ?? []).filter((p) => p !== partId),
      partQuotedSpecs: nextSpecs,
    });
    // Drop the part-edit state in case the user was mid-edit on it.
    if (editingPartId === partId) {
      setEditingPartId(null);
      setPartDraft(null);
    }
    await load();
  }

  async function handlePriceChange(partId: string, tierIndex: number, value: number) {
    if (!quote) return;
    const spec = quote.partQuotedSpecs?.[partId];
    if (!spec) return;
    const nextPrices = [...(spec.pricePerTier ?? [])];
    // Pad if the snapshot is shorter than the quote's tier count (defensive
    // — shouldn't happen with the seeder, but keeps re-priced quotes whole).
    while (nextPrices.length < (quote.qtyTiers?.length ?? 0)) nextPrices.push(0);
    nextPrices[tierIndex] = value;
    const nextSpecs = {
      ...(quote.partQuotedSpecs ?? {}),
      [partId]: { ...spec, pricePerTier: nextPrices },
    };
    await updateQuote(quote.id, { partQuotedSpecs: nextSpecs });
    await load();
  }

  async function handleReprice(partId: string) {
    if (!quote) return;
    const part = partsById[partId];
    if (!part) {
      window.alert('Could not load the part record to reprice from.');
      return;
    }
    const seeded = seedSnapshotFromPart(part, quote.qtyTiers ?? [], machinesByName);
    const existing = quote.partQuotedSpecs?.[partId];
    const willChange = !existing
      || (existing.pricePerTier ?? []).some(
        (p, i) => p !== (seeded.pricePerTier[i] ?? 0),
      )
      || existing.pricePerTier?.length !== seeded.pricePerTier.length;
    if (willChange && existing) {
      const ok = window.confirm(
        'Reprice will overwrite the current per-tier prices with a fresh default markup. Continue?',
      );
      if (!ok) return;
    }
    const nextSpecs = {
      ...(quote.partQuotedSpecs ?? {}),
      [partId]: { ...existing, ...seeded },
    };
    await updateQuote(quote.id, { partQuotedSpecs: nextSpecs });
    await load();
  }

  function handleEnterPartEdit(partId: string) {
    const part = partsById[partId];
    if (!part) return;
    setEditingPartId(partId);
    setPartDraft(part);
  }

  function handleCancelPartEdit() {
    setEditingPartId(null);
    setPartDraft(null);
  }

  async function handleSavePart(p: Partial<Part> & { type: PartType }) {
    if (!editingPartId) return;
    // Strip baseline + id keys before forwarding — mirrors PartDetailPage's
    // approach so updatePart's mutable-fields contract is respected.
    const baselineKeys = new Set([
      'id',
      'createdAt',
      'updatedAt',
      'createdBy',
      'updatedBy',
      '_v',
      '_audit',
      '_deleted',
      '_deletedAt',
      '_deletedBy',
      '_deletedReason',
    ]);
    const mutable: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(p)) {
      if (!baselineKeys.has(k)) mutable[k] = v;
    }
    await updatePart(editingPartId, mutable as Parameters<typeof updatePart>[1]);
    setEditingPartId(null);
    setPartDraft(null);
    await load();
  }

  async function handleSavePartAttachments(partId: string, attachedDrawings: string[]) {
    await updatePart(partId, { attachedDrawings });
    setAttachmentPickerPartId(null);
    await load();
  }

  async function handleMarkSent() {
    if (!quote) return;
    if (quote.status !== 'draft') {
      window.alert(`Can only mark draft quotes as sent. Current status: ${quote.status}.`);
      return;
    }
    const ok = window.confirm(
      'Mark this quote as sent? Follow-up cadence will start, and the status badge will flip to Sent.',
    );
    if (!ok) return;
    const now = new Date().toISOString();
    // +7d nudge gets us into the day_7 stage automatically. Justin can
    // pause / adjust via the follow-up controls (M2 Phase 5.5+).
    const nextDue = new Date();
    nextDue.setDate(nextDue.getDate() + 7);
    await updateQuote(quote.id, {
      status: 'sent',
      sentAt: now,
      followUp: {
        ...(quote.followUp ?? { draftsHistory: [], stage: 'paused', paused: true }),
        nextDueDate: nextDue.toISOString().slice(0, 10),
        stage: 'day_7',
        paused: false,
      },
    });
    await load();
  }

  async function handleDelete() {
    if (!quote) return;
    if (!window.confirm(`Delete quote ${quote.number}?\n\nSoft-deleted — recoverable via Firebase Console.`)) return;
    try {
      const { softDeleteQuote } = await import('@/shared/crm');
      await softDeleteQuote(quote.id, 'user-deleted-from-detail');
      navigate('/quotes');
    } catch (err) {
      window.alert(`Delete failed: ${(err as Error).message}`);
    }
  }

  return (
    <div className="max-w-6xl mx-auto">
      <button
        type="button"
        onClick={() => navigate('/quotes')}
        className="text-xs text-text-mute hover:text-orange mb-2"
      >
        ← All quotes
      </button>

      <QuoteHeader
        quote={quote}
        customer={customer}
        onCustomerClick={() => customer && navigate(`/customers/${customer.id}`)}
      />

      {/* Tabs */}
      <div className="border-b border-line mt-4 mb-4 flex flex-wrap">
        {TABS.map((t) => {
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${
                active
                  ? 'border-orange text-orange'
                  : 'border-transparent text-text-mute hover:text-navy'
              }`}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'builder' && (
        <BuilderTab
          quote={quote}
          partsById={partsById}
          machinesByName={machinesByName}
          editingPartId={editingPartId}
          partDraft={partDraft}
          onQtyTiersChange={handleQtyTiersChange}
          onOpenAddNewPart={() => setAddPartModalOpen(true)}
          onOpenPicker={() => setPickerOpen(true)}
          onEditPart={handleEnterPartEdit}
          onCancelEditPart={handleCancelPartEdit}
          onSavePart={handleSavePart}
          onPartDraftChange={(updates) =>
            setPartDraft((prev) =>
              prev ? { ...prev, ...updates, type: prev.type } : prev,
            )
          }
          onReprice={handleReprice}
          onRemovePart={handleRemovePart}
          onPriceChange={handlePriceChange}
          onOpenAttachmentPicker={(pid) => setAttachmentPickerPartId(pid)}
          onQuoteChanged={load}
        />
      )}

      {tab === 'pricing' && (
        <PricingTab quote={quote} partsById={partsById} machinesByName={machinesByName} />
      )}

      {tab === 'activity' && <ActivityTab quote={quote} />}

      {/* Bottom action row — full-width separator, sticky-feeling */}
      <div className="mt-6 border-t border-line pt-3 flex items-center justify-between gap-2 flex-wrap">
        <div className="text-[11px] text-text-mute">
          Changes save automatically as you edit. Last saved {fmtRelativeAgo(quote.updatedAt)}.
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <QuotePdfActions
            quote={quote}
            customer={customer}
            parts={Object.values(partsById)}
            onPreview={(blob) => {
              setPdfPreviewBlob(blob);
              setPdfPreviewOpen(true);
            }}
          />
          <span className="text-text-mute opacity-50">|</span>
          <button
            type="button"
            onClick={handleDelete}
            className="px-3 py-1.5 text-sm rounded border border-line-dark text-text-mute hover:bg-bg hover:text-orange"
          >
            Delete
          </button>
          <button
            type="button"
            onClick={handleMarkSent}
            disabled={quote.status !== 'draft'}
            className="px-4 py-1.5 bg-orange text-white text-sm rounded font-semibold hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
            title={
              quote.status === 'draft'
                ? 'Flip to Sent and start the 7-day follow-up cadence'
                : `Status is "${quote.status}" — Mark sent only works from Draft`
            }
          >
            Mark sent →
          </button>
        </div>
      </div>

      {/* PDF preview modal — opened by QuotePdfActions's "Generate PDF" button. */}
      <PdfViewerModal
        open={pdfPreviewOpen}
        onClose={() => setPdfPreviewOpen(false)}
        source={pdfPreviewBlob}
        title={`Quote ${quote.number || quote.id}.pdf`}
        downloadFilename={`Quote-${quote.number || quote.id}.pdf`}
      />

      {/* Pick-an-existing-part modal */}
      <ExistingPartPickerModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        excludeIds={quote.partIds ?? []}
        onPick={handleAddExistingPart}
      />

      {/* Reuse NewPartModal for the create-and-attach flow */}
      <NewPartModal
        open={addPartModalOpen}
        onClose={() => setAddPartModalOpen(false)}
        onCreated={handlePartCreated}
      />

      {/* Per-part attachment picker — opened from each part row's 📎 button */}
      {attachmentPickerPartId && partsById[attachmentPickerPartId] && (
        <PartAttachmentPickerModal
          open
          onClose={() => setAttachmentPickerPartId(null)}
          quote={quote}
          part={partsById[attachmentPickerPartId]}
          onSave={(urls) => handleSavePartAttachments(attachmentPickerPartId, urls)}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Header — inline navy gradient (CustomerBanner-lite)
// ─────────────────────────────────────────────────────────────

interface QuoteHeaderProps {
  quote: Quote;
  customer: Customer | null;
  onCustomerClick: () => void;
}

function QuoteHeader({ quote, customer, onCustomerClick }: QuoteHeaderProps) {
  const totals = useMemo(() => computeTotalsByTier(quote), [quote]);
  // Bottom (lowest) tier — that's the smallest qty, highest unit price,
  // and the number Justin usually quotes first.
  const previewTotal = totals[0];
  const jobMeta = JOB_TYPE_LABELS[quote.jobType] ?? { label: quote.jobType, icon: '·' };

  return (
    <div className="bg-gradient-to-br from-navy to-[#2A3B52] text-white rounded shadow px-5 py-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-xl sm:text-2xl font-bold font-mono">{quote.number}</h1>
            <QuoteStatusBadge status={quote.status} size="md" />
            {quote.marginWarning && (
              <span
                className="text-[10px] px-2 py-0.5 rounded-full bg-orange/25 text-white font-semibold"
                title="At least one tier margin is below threshold"
              >
                ⚠ margin
              </span>
            )}
          </div>

          {/* Customer line — clickable */}
          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
            {customer ? (
              <>
                <button
                  type="button"
                  onClick={onCustomerClick}
                  className="text-sm font-semibold hover:underline truncate text-left"
                  title={`Open ${customer.companyName}`}
                >
                  {customer.companyName}
                </button>
                <TierBadge tier={customer.tier} size="sm" />
                {customer.code && (
                  <span className="text-[11px] font-mono opacity-70">{customer.code}</span>
                )}
              </>
            ) : (
              <span className="text-sm opacity-70 italic">
                {quote.customerNameSnapshot || 'No customer linked'}
              </span>
            )}
          </div>

          {/* Meta row */}
          <div className="text-[11px] opacity-85 mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
            <span title={jobMeta.label}>
              {jobMeta.icon} <span>{jobMeta.label}</span>
            </span>
            {quote.dueDate && (
              <>
                <span className="opacity-60">·</span>
                <span>
                  Due <time dateTime={quote.dueDate}>{new Date(quote.dueDate).toLocaleDateString()}</time>
                </span>
              </>
            )}
            <span className="opacity-60">·</span>
            <span title={new Date(quote.updatedAt).toLocaleString()}>
              Updated {fmtRelativeAgo(quote.updatedAt)}
            </span>
          </div>
        </div>

        {/* Right side: total preview */}
        <div className="text-right shrink-0">
          <div className="text-[10px] uppercase tracking-wide opacity-70">
            Total · tier {quote.qtyTiers?.[0] ?? '—'}
          </div>
          <div className="text-2xl font-bold font-mono">
            {previewTotal === undefined || previewTotal === 0
              ? <span className="opacity-50">—</span>
              : fmtCurrency(previewTotal)}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Builder tab
// ─────────────────────────────────────────────────────────────

interface BuilderTabProps {
  quote: Quote;
  partsById: Record<string, Part>;
  machinesByName: Record<string, Machine>;
  editingPartId: string | null;
  partDraft: (Partial<Part> & { type: PartType }) | null;
  onQtyTiersChange: (raw: string) => void | Promise<void>;
  onOpenAddNewPart: () => void;
  onOpenPicker: () => void;
  onEditPart: (partId: string) => void;
  onCancelEditPart: () => void;
  onSavePart: (p: Partial<Part> & { type: PartType }) => void | Promise<void>;
  onPartDraftChange: (updates: Partial<Part>) => void;
  onReprice: (partId: string) => void | Promise<void>;
  onRemovePart: (partId: string) => void | Promise<void>;
  onPriceChange: (partId: string, tierIndex: number, value: number) => void | Promise<void>;
  onOpenAttachmentPicker: (partId: string) => void;
  /** Refetch the quote after a drag-drop upload or attachment-panel change. */
  onQuoteChanged: () => void | Promise<void>;
}

function BuilderTab({
  quote,
  partsById,
  machinesByName,
  editingPartId,
  partDraft,
  onQtyTiersChange,
  onOpenAddNewPart,
  onOpenPicker,
  onEditPart,
  onCancelEditPart,
  onSavePart,
  onPartDraftChange,
  onReprice,
  onRemovePart,
  onPriceChange,
  onOpenAttachmentPicker,
  onQuoteChanged,
}: BuilderTabProps) {
  const [tiersRaw, setTiersRaw] = useState((quote.qtyTiers ?? []).join(' / '));
  const [tiersError, setTiersError] = useState<string | null>(null);
  const [tiersWarning, setTiersWarning] = useState(false);

  // Page-level drag overlay state — flips on when the user drags files over
  // anywhere in the Builder tab. Drop fires uploadQuoteAttachment for each
  // file, then bumps the quote.attachedDrawings list via updateQuote. We
  // count dragenter/dragleave events to defeat the browser-fires-multiple-
  // dragleave-for-nested-children behavior; only a true exit decrements to 0.
  const [dragDepth, setDragDepth] = useState(0);
  const [dragUploading, setDragUploading] = useState(false);
  const [dragError, setDragError] = useState<string | null>(null);

  // Reset the local edit buffer whenever the canonical tiers change
  // (post-save reload, or another browser tab edits the quote).
  useEffect(() => {
    setTiersRaw((quote.qtyTiers ?? []).join(' / '));
  }, [quote.qtyTiers]);

  function handleTiersBlur() {
    const parsed = parseQtyTiers(tiersRaw);
    if (!parsed) {
      setTiersError('1-5 positive integers, separated by / , or space.');
      return;
    }
    setTiersError(null);
    // Flag a warning when the tier *count* changes — that's when sticky
    // pricePerTier arrays go misaligned and need re-pricing.
    const lengthChanged = parsed.length !== (quote.qtyTiers?.length ?? 0);
    setTiersWarning(lengthChanged);
    void onQtyTiersChange(tiersRaw);
  }

  const partIds = quote.partIds ?? [];

  // ───── Page-level drag handlers ─────
  // We bind onDragEnter/Over/Leave/Drop on the wrapper div so dropping
  // anywhere in the Builder tab uploads to the quote pool. The
  // QuoteAttachmentsPanel still has its own DrawingUpload drop zone for
  // discoverability — those events stop at the panel since it preventDefaults
  // before we see them. But a user who drags onto a part row or the totals
  // card still ends up in the right place.
  function handleDragEnter(e: React.DragEvent<HTMLDivElement>) {
    // Only react to drags carrying files — text/HTML drags from elsewhere
    // in the app would otherwise spuriously trigger the overlay.
    if (!Array.from(e.dataTransfer.types).includes('Files')) return;
    e.preventDefault();
    setDragDepth((d) => d + 1);
  }
  function handleDragOver(e: React.DragEvent<HTMLDivElement>) {
    if (!Array.from(e.dataTransfer.types).includes('Files')) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  }
  function handleDragLeave(e: React.DragEvent<HTMLDivElement>) {
    if (!Array.from(e.dataTransfer.types).includes('Files')) return;
    e.preventDefault();
    setDragDepth((d) => Math.max(0, d - 1));
  }
  async function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    if (!Array.from(e.dataTransfer.types).includes('Files')) return;
    e.preventDefault();
    setDragDepth(0);
    const files = Array.from(e.dataTransfer.files ?? []);
    if (files.length === 0) return;

    setDragUploading(true);
    setDragError(null);
    const completed: string[] = [];
    const errors: string[] = [];
    for (const file of files) {
      try {
        const result = await uploadQuoteAttachment(quote.id, file);
        completed.push(result.url);
      } catch (err) {
        errors.push(`${file.name}: ${(err as Error).message}`);
      }
    }
    if (completed.length > 0) {
      const current = (quote as Quote & { attachedDrawings?: string[] }).attachedDrawings ?? [];
      const merged = [...current, ...completed];
      try {
        await updateQuote(quote.id, {
          attachedDrawings: merged,
        } as Parameters<typeof updateQuote>[1]);
        await onQuoteChanged();
      } catch (err) {
        errors.push(`Saving to quote: ${(err as Error).message}`);
      }
    }
    if (errors.length > 0) setDragError(errors.join(' · '));
    setDragUploading(false);
  }

  return (
    <div
      className="space-y-4 relative"
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Page-level drag overlay — fires when files are dragged anywhere over
          the Builder tab. The QuoteAttachmentsPanel below has its own focused
          drop zone for the discovery-first path; this catches the "I just
          dragged a PDF onto the page" case so nothing falls on the floor. */}
      {dragDepth > 0 && (
        <div className="fixed inset-0 z-40 bg-navy/50 backdrop-blur-sm flex items-center justify-center pointer-events-none">
          <div className="bg-paper border-4 border-dashed border-orange rounded-lg px-12 py-10 text-center shadow-xl max-w-lg">
            <div className="text-5xl mb-3">📎</div>
            <div className="text-xl font-bold text-navy">
              Drop drawings to attach to this quote
            </div>
            <div className="text-xs text-text-mute mt-2">
              PDF · drawings · CAD · 25MB max each
            </div>
          </div>
        </div>
      )}

      {dragUploading && (
        <div className="bg-blue/10 border-l-4 border-blue text-text px-3 py-2 text-xs">
          Uploading dropped drawings to the quote pool…
        </div>
      )}
      {dragError && (
        <div className="bg-orange/10 border-l-4 border-orange text-text px-3 py-2 text-xs flex items-center justify-between gap-2">
          <span>⚠ {dragError}</span>
          <button
            type="button"
            onClick={() => setDragError(null)}
            className="text-orange font-semibold hover:underline shrink-0"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Quote attachments pool — sits at the top of Builder so it reads as
          "this quote's traveler" before any pricing details. */}
      <QuoteAttachmentsPanel quote={quote} onChanged={() => void onQuoteChanged()} />

      {/* Quote-level qty tiers */}
      <div className="bg-paper border border-line rounded p-4">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
          <h3 className="font-semibold text-navy">Quantity tiers</h3>
          <span className="text-[11px] text-text-mute">
            Quote-level — parts can override on their own record.
          </span>
        </div>
        <input
          type="text"
          value={tiersRaw}
          onChange={(e) => {
            setTiersRaw(e.target.value);
            setTiersError(null);
          }}
          onBlur={handleTiersBlur}
          placeholder="10 / 50 / 100"
          className="w-full sm:w-64 px-2 py-1.5 border border-line-dark rounded text-sm font-mono focus:outline-none focus:border-orange"
        />
        {tiersError && (
          <div className="mt-2 text-xs text-orange">⚠ {tiersError}</div>
        )}
        {tiersWarning && (
          <div className="mt-2 bg-amber/10 border-l-4 border-amber text-text px-3 py-2 text-xs">
            Tiers changed — re-price each part to refresh per-tier totals.
          </div>
        )}
      </div>

      {/* Parts section */}
      <div className="bg-paper border border-line rounded p-4">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <h3 className="font-semibold text-navy">Parts ({partIds.length})</h3>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onOpenPicker}
              className="px-3 py-1.5 text-sm rounded border border-line-dark text-text hover:bg-bg"
            >
              + Add existing part
            </button>
            <button
              type="button"
              onClick={onOpenAddNewPart}
              className="px-3 py-1.5 bg-orange text-white text-sm rounded font-semibold hover:opacity-90"
            >
              + Create new part
            </button>
          </div>
        </div>

        {partIds.length === 0 ? (
          <div className="text-sm text-text-mute italic text-center py-6">
            No parts on this quote yet. Add an existing part or create a new one above.
          </div>
        ) : (
          <ul className="space-y-3">
            {partIds.map((pid) => {
              const part = partsById[pid];
              const spec = quote.partQuotedSpecs?.[pid];
              const isEditing = editingPartId === pid;
              return (
                <li key={pid}>
                  <PartLineRow
                    quote={quote}
                    partId={pid}
                    part={part}
                    spec={spec}
                    onPriceChange={(tier, val) => onPriceChange(pid, tier, val)}
                    onEdit={() => onEditPart(pid)}
                    onReprice={() => onReprice(pid)}
                    onRemove={() => onRemovePart(pid)}
                    onOpenAttachmentPicker={() => onOpenAttachmentPicker(pid)}
                    isEditing={isEditing}
                  />
                  {isEditing && partDraft && (
                    <PartWizard
                      embedded
                      part={partDraft}
                      onChange={onPartDraftChange}
                      onSave={onSavePart}
                      onCancel={onCancelEditPart}
                    />
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Totals card */}
      <TotalsCard quote={quote} partsById={partsById} machinesByName={machinesByName} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Part line row
// ─────────────────────────────────────────────────────────────

interface PartLineRowProps {
  quote: Quote;
  partId: string;
  part: Part | undefined;
  spec: PartQuotedSpec | undefined;
  onPriceChange: (tierIndex: number, value: number) => void;
  onEdit: () => void;
  onReprice: () => void;
  onRemove: () => void;
  onOpenAttachmentPicker: () => void;
  isEditing: boolean;
}

function PartLineRow({
  quote,
  part,
  spec,
  onPriceChange,
  onEdit,
  onReprice,
  onRemove,
  onOpenAttachmentPicker,
  isEditing,
}: PartLineRowProps) {
  const tiers = quote.qtyTiers ?? [];
  const prices = spec?.pricePerTier ?? [];

  if (!part) {
    return (
      <div className="border border-line rounded p-3 bg-bg/40 text-sm text-text-mute italic">
        Part record missing (may have been soft-deleted).{' '}
        <button
          type="button"
          onClick={onRemove}
          className="text-orange hover:underline ml-2 text-xs"
        >
          Remove from quote
        </button>
      </div>
    );
  }

  const typeMeta = PART_TYPE_META[part.type];

  return (
    <div className={`border border-line rounded p-3 ${isEditing ? 'border-orange/40' : ''}`}>
      {/* Two-column layout: thumbnail on the left (md+ only — hidden on
          mobile to keep the row from getting cramped), everything else
          shifted right of it. */}
      <div className="flex items-start gap-3">
        <div className="hidden md:block shrink-0">
          <PartCardImage part={part} width={72} />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <PartTypeBadge type={part.type} size="sm" />
                <span className="font-mono font-bold text-navy">{part.number}</span>
                <span className="text-[10px] text-text-mute uppercase tracking-wide">
                  {typeMeta.shortLabel}
                </span>
              </div>
              <div className="text-xs text-text-mute mt-0.5 truncate" title={part.description}>
                {part.description}
              </div>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <button
                type="button"
                onClick={onOpenAttachmentPicker}
                className="text-xs px-2 py-1 rounded border border-line-dark text-text hover:bg-bg"
                title="Pick which quote-pool drawings apply to this part"
              >
                📎 {part.attachedDrawings?.length ?? 0}
              </button>
              <button
                type="button"
                onClick={onEdit}
                disabled={isEditing}
                className="text-xs px-2 py-1 rounded border border-line-dark text-text hover:bg-bg disabled:opacity-50"
                title="Edit this part's spec inline"
              >
                {isEditing ? 'Editing…' : '✏ Edit specs'}
              </button>
              <button
                type="button"
                onClick={onReprice}
                className="text-xs px-2 py-1 rounded border border-line-dark text-text hover:bg-bg"
                title="Recompute per-tier prices from this part's latest material cost"
              >
                🔄 Reprice
              </button>
              <button
                type="button"
                onClick={onRemove}
                className="text-xs px-2 py-1 rounded border border-line-dark text-text-mute hover:text-orange hover:bg-bg"
                title="Remove this part from the quote"
              >
                ✖ Remove
              </button>
            </div>
          </div>

          {/* Inline price-per-tier editor */}
          <div className="mt-1 border-t border-line pt-2">
            <div className="text-[11px] font-semibold text-text-mute uppercase tracking-wide mb-1.5">
              Price per tier
            </div>
            {tiers.length === 0 ? (
              <div className="text-xs text-text-mute italic">No tiers set on the quote yet.</div>
            ) : (
              <div className="flex flex-wrap gap-3">
                {tiers.map((qty, t) => (
                  <PriceCell
                    key={`${qty}-${t}`}
                    tierLabel={`@ ${qty}`}
                    value={prices[t] ?? 0}
                    onCommit={(v) => onPriceChange(t, v)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

interface PriceCellProps {
  tierLabel: string;
  value: number;
  onCommit: (val: number) => void;
}

/**
 * Per-tier price input. Comma-formatted while idle, raw number while
 * focused — committed on blur so we don't fire a write per keystroke.
 */
function PriceCell({ tierLabel, value, onCommit }: PriceCellProps) {
  const [focused, setFocused] = useState(false);
  const [raw, setRaw] = useState(value.toString());

  // Re-sync from prop whenever the canonical price changes (post-reprice,
  // post-save reload) — but only when the input isn't actively being edited.
  useEffect(() => {
    if (!focused) setRaw(value.toString());
  }, [value, focused]);

  function commit() {
    setFocused(false);
    const parsed = Number.parseFloat(raw.replace(/,/g, ''));
    if (!Number.isFinite(parsed) || parsed < 0) {
      // Reject — revert the buffer to the canonical value.
      setRaw(value.toString());
      return;
    }
    if (parsed === value) {
      // No-op — just reformat the buffer.
      setRaw(parsed.toString());
      return;
    }
    onCommit(parsed);
  }

  const displayValue = focused
    ? raw
    : value === 0
      ? ''
      : value.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });

  return (
    <label className="block">
      <span className="block text-[10px] font-mono text-text-mute mb-0.5">{tierLabel}</span>
      <div className="relative">
        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-text-mute text-xs">$</span>
        <input
          type="text"
          inputMode="decimal"
          value={displayValue}
          onChange={(e) => setRaw(e.target.value)}
          onFocus={() => {
            setFocused(true);
            setRaw(value === 0 ? '' : value.toString());
          }}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
          }}
          placeholder="0.00"
          className="w-28 pl-5 pr-2 py-1 border border-line-dark rounded text-sm font-mono focus:outline-none focus:border-orange"
        />
      </div>
    </label>
  );
}

// ─────────────────────────────────────────────────────────────
// Totals card (Builder tab footer + Pricing tab header)
// ─────────────────────────────────────────────────────────────

interface TotalsCardProps {
  quote: Quote;
  partsById: Record<string, Part>;
  /** Machines keyed by name — drives the live cost basis for margin math.
   *  Empty map is OK; cost lines that depend on rates fall back to $0. */
  machinesByName: Record<string, Machine>;
  large?: boolean;
}

function TotalsCard({ quote, partsById, machinesByName, large = false }: TotalsCardProps) {
  const totals = useMemo(() => computeTotalsByTier(quote), [quote]);
  // Switched from material-only basis (computeMaterialCostByTier) to the
  // full live cost engine — material + amortized setup + machine runtime +
  // outside services. That's the honest cost basis. Margins now reflect
  // shop reality, not just material drift.
  const costs = useMemo(
    () => computeFullCostByTier(quote, partsById, machinesByName),
    [quote, partsById, machinesByName],
  );
  const margins = useMemo(
    () => computeMarginsByTier(totals, costs),
    [totals, costs],
  );

  const tiers = quote.qtyTiers ?? [];
  // margins[t] can now be null ("no data" — both total + cost are 0). Skip
  // those when deciding if the warning chip should show, otherwise the
  // empty-quote case would always look like a margin crisis.
  const hasWarning = totals.some((total, t) => {
    const m = margins[t];
    return total > 0 && m !== null && m < MARGIN_WARNING_THRESHOLD;
  });

  return (
    <div className="bg-paper border border-line rounded p-4">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <h3 className={`font-semibold text-navy ${large ? 'text-lg' : ''}`}>
          Totals + margin
        </h3>
        {hasWarning && (
          <span className="text-[11px] px-2 py-0.5 rounded-full bg-orange/15 text-orange font-semibold">
            ⚠ Margin below {MARGIN_WARNING_THRESHOLD}% on at least one tier
          </span>
        )}
      </div>

      {tiers.length === 0 ? (
        <div className="text-sm text-text-mute italic">No tiers set yet.</div>
      ) : (
        <div className={`grid gap-3 ${large ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' : 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-5'}`}>
          {tiers.map((qty, t) => (
            <TotalsTierCell
              key={`${qty}-${t}`}
              qty={qty}
              total={totals[t] ?? 0}
              cost={costs[t] ?? 0}
              margin={margins[t] ?? null}
              large={large}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface TotalsTierCellProps {
  qty: number;
  total: number;
  cost: number;
  /** null = no data (no parts priced for this tier). Distinct from 0% margin. */
  margin: number | null;
  large: boolean;
}

function TotalsTierCell({ qty, total, cost, margin, large }: TotalsTierCellProps) {
  // Color rules per spec: >=35 green, 25-35 amber, <25 orange + ⚠
  // Null margin = no data, rendered as "—" with no warning tint.
  let marginCls = 'text-text-mute';
  let warn = false;
  if (total > 0 && margin !== null) {
    if (margin >= 35) marginCls = 'text-green';
    else if (margin >= MARGIN_WARNING_THRESHOLD) marginCls = 'text-amber';
    else {
      marginCls = 'text-orange';
      warn = true;
    }
  }
  return (
    <div className="border border-line rounded p-3">
      <div className="text-[10px] uppercase tracking-wide text-text-mute font-semibold">
        @ {qty}
      </div>
      <div className={`font-mono font-bold text-navy ${large ? 'text-2xl' : 'text-lg'}`}>
        {total === 0 ? <span className="opacity-50">—</span> : fmtCurrency(total)}
      </div>
      <div className="text-[11px] text-text-mute font-mono mt-0.5">
        cost {fmtCurrency(cost)}
      </div>
      <div className={`text-xs font-semibold mt-1 ${marginCls}`}>
        {warn && <span className="mr-1">⚠</span>}
        {margin === null || total === 0 ? '— margin' : `${margin.toFixed(1)}% margin`}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Pricing tab
// ─────────────────────────────────────────────────────────────

function PricingTab({
  quote,
  partsById,
  machinesByName,
}: {
  quote: Quote;
  partsById: Record<string, Part>;
  machinesByName: Record<string, Machine>;
}) {
  const tiers = quote.qtyTiers ?? [];
  const partIds = quote.partIds ?? [];

  return (
    <div className="space-y-4">
      <TotalsCard quote={quote} partsById={partsById} machinesByName={machinesByName} large />

      <div className="bg-paper border border-line rounded p-4">
        <h3 className="font-semibold text-navy mb-3">Per-part breakdown</h3>
        {partIds.length === 0 ? (
          <div className="text-sm text-text-mute italic text-center py-4">
            No parts on this quote.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-bg text-text-mute">
                <tr>
                  <th
                    scope="col"
                    className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide"
                  >
                    Part
                  </th>
                  {tiers.map((qty, t) => (
                    <th
                      key={`${qty}-${t}`}
                      scope="col"
                      className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide whitespace-nowrap"
                    >
                      @ {qty}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {partIds.map((pid) => (
                  <PerPartRow
                    key={pid}
                    quote={quote}
                    partId={pid}
                    part={partsById[pid]}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function PerPartRow({
  quote,
  partId,
  part,
}: {
  quote: Quote;
  partId: string;
  part: Part | undefined;
}) {
  const tiers = quote.qtyTiers ?? [];
  const spec = quote.partQuotedSpecs?.[partId];
  const prices = spec?.pricePerTier ?? [];
  const perPiece = part?.materialCost?.perPiece ?? [];

  return (
    <tr className="border-t border-line">
      <td className="px-3 py-2">
        <div className="flex items-center gap-2 min-w-0">
          {part && <PartTypeBadge type={part.type} size="sm" />}
          <span className="font-mono font-semibold text-navy">{part?.number ?? '?'}</span>
        </div>
        <div className="text-[11px] text-text-mute truncate max-w-[280px]">
          {part?.description ?? '(missing part record)'}
        </div>
      </td>
      {tiers.map((qty, t) => {
        const price = prices[t] ?? 0;
        const cost = perPiece[t] ?? 0;
        const margin = price > 0 ? ((price - cost) / price) * 100 : 0;
        let mcls = 'text-text-mute';
        if (price > 0) {
          if (margin >= 35) mcls = 'text-green';
          else if (margin >= MARGIN_WARNING_THRESHOLD) mcls = 'text-amber';
          else mcls = 'text-orange';
        }
        return (
          <td
            key={`${qty}-${t}`}
            className="px-3 py-2 text-right font-mono whitespace-nowrap"
          >
            <div className="font-semibold text-text">
              {price === 0 ? <span className="text-text-mute">—</span> : fmtCurrency(price, { fractionDigits: 2 })}
            </div>
            <div className={`text-[11px] font-semibold ${mcls}`}>
              {price === 0 ? '—' : `${margin.toFixed(1)}%`}
            </div>
          </td>
        );
      })}
    </tr>
  );
}

// ─────────────────────────────────────────────────────────────
// Activity tab — _audit[] timeline
// ─────────────────────────────────────────────────────────────

function ActivityTab({ quote }: { quote: Quote }) {
  const entries: AuditEntry[] = quote._audit ?? [];

  if (entries.length === 0) {
    return (
      <div className="bg-paper border border-line rounded p-6 text-center text-text-mute">
        <div className="text-sm">No activity yet.</div>
        <div className="text-[11px] mt-1 italic">
          Audit entries populate once the auth/role flow lands (see crm.ts).
        </div>
      </div>
    );
  }

  const sorted = [...entries].sort(
    (a, b) => new Date(b.ts).getTime() - new Date(a.ts).getTime(),
  );

  return (
    <div className="bg-paper border border-line rounded p-4">
      <ol className="space-y-3">
        {sorted.map((e, i) => (
          <li key={`${e.ts}-${i}`} className="flex gap-3">
            <div className="shrink-0 w-2 h-2 rounded-full bg-orange mt-1.5" aria-hidden />
            <div className="min-w-0 flex-1">
              <div className="text-sm text-text">
                <span className="font-semibold capitalize">{e.action.replace(/_/g, ' ')}</span>
                {e.fields && Object.keys(e.fields).length > 0 && (
                  <span className="text-text-mute"> · {Object.keys(e.fields).join(', ')}</span>
                )}
              </div>
              <div className="text-[11px] text-text-mute mt-0.5 flex flex-wrap items-center gap-2">
                <time dateTime={e.ts} className="font-mono">
                  {new Date(e.ts).toLocaleString()}
                </time>
                <span className="opacity-60">·</span>
                <span>{e.user}</span>
                <span className="opacity-60">·</span>
                <span className="italic">{e.source}</span>
              </div>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Existing-part picker modal (used by "+ Add existing part")
// ─────────────────────────────────────────────────────────────

interface ExistingPartPickerModalProps {
  open: boolean;
  onClose: () => void;
  excludeIds: string[];
  onPick: (part: Part) => void | Promise<void>;
}

function ExistingPartPickerModal({
  open,
  onClose,
  excludeIds,
  onPick,
}: ExistingPartPickerModalProps) {
  const [parts, setParts] = useState<Part[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState('');

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setSearchInput('');
    listParts()
      .then((list) => {
        if (!cancelled) setParts(list);
      })
      .catch(() => {
        if (!cancelled) setParts([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  // Escape closes the modal — same contract as the other modals.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  const filtered = useMemo(() => {
    const needle = searchInput.trim().toLowerCase();
    const excluded = new Set(excludeIds);
    return parts.filter((p) => {
      if (excluded.has(p.id)) return false;
      if (!needle) return true;
      return (
        p.number.toLowerCase().includes(needle) ||
        p.description.toLowerCase().includes(needle)
      );
    });
  }, [parts, searchInput, excludeIds]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-navy/50 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-existing-part-title"
    >
      <div
        className="bg-paper rounded shadow-lg w-full max-w-[560px] max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3 border-b border-line flex items-center justify-between bg-navy text-paper shrink-0">
          <h2 id="add-existing-part-title" className="font-bold text-base">
            Add an existing part
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-paper/80 hover:text-paper text-lg leading-none"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className="p-4 shrink-0">
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by part number or description…"
            autoFocus
            className="w-full px-3 py-2 border border-line-dark rounded text-sm focus:outline-none focus:border-orange"
          />
          <div className="text-[11px] text-text-mute mt-1.5">
            {loading
              ? 'Loading parts…'
              : `${filtered.length} match${filtered.length === 1 ? '' : 'es'}${excludeIds.length > 0 ? ` · ${excludeIds.length} already on quote` : ''}`}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-4 pb-4">
          {!loading && filtered.length === 0 && (
            <div className="text-sm text-text-mute italic text-center py-6">
              {searchInput.trim()
                ? `No parts match "${searchInput}".`
                : 'No more parts to add — create a new one from the Builder tab.'}
            </div>
          )}
          <ul className="flex flex-col gap-1.5">
            {filtered.map((p) => (
              <li key={p.id}>
                <button
                  type="button"
                  onClick={() => void onPick(p)}
                  className="w-full text-left border border-line rounded p-2 hover:border-orange hover:shadow-sm transition-all bg-paper flex items-center gap-3"
                >
                  <PartTypeBadge type={p.type} size="sm" />
                  <div className="min-w-0 flex-1">
                    <div className="font-mono font-semibold text-navy truncate">{p.number}</div>
                    <div className="text-[11px] text-text-mute truncate" title={p.description}>
                      {p.description}
                    </div>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
